import { useSignal } from "@preact/signals";
import type { PageArgs } from "bunfx";
import { ClientError, navigateTo } from "bunfx";
import { AuthLayout } from "../components/layout";
import { Button, Form, type FormErrors, Password } from "../components/ui";
import { usePageTitle } from "../hooks";
import { rpc } from "../rpc";

type LoaderData =
  | { valid: true; userId: string; token: string }
  | { valid: false; error: string; isExpired: boolean };

export async function load({
  searchParams,
}: {
  searchParams: Record<string, string>;
}): Promise<LoaderData> {
  const userId = searchParams.user;
  const token = searchParams.token;

  if (!userId || !token) {
    return { valid: false, error: "Invalid reset link", isExpired: false };
  }

  // We don't validate the token on load - just check params exist
  // Token validation happens on form submit to avoid timing attacks
  return { valid: true, userId, token };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Reset Password");
  const data = state.value;

  if (!data.valid) {
    return <ErrorView error={data.error} isExpired={data.isExpired} />;
  }

  return <PasswordResetForm userId={data.userId} token={data.token} />;
}

// ============================================================================
// Error View
// ============================================================================

function ErrorView({
  error,
  isExpired,
}: {
  error: string;
  isExpired: boolean;
}) {
  return (
    <AuthLayout
      title={isExpired ? "Link expired" : "Invalid link"}
      subtitle={
        isExpired
          ? "Your password reset link has expired. Please request a new one."
          : error
      }
      hideLogo
    >
      <div class="flex flex-col gap-3">
        <Button class="w-full" onClick={() => navigateTo("/forgotpassword")}>
          Request new link
        </Button>
        <Button
          variant="secondary"
          class="w-full"
          onClick={() => navigateTo("/login")}
        >
          Back to sign in
        </Button>
      </div>
    </AuthLayout>
  );
}

// ============================================================================
// Password Reset Form
// ============================================================================

function PasswordResetForm({
  userId,
  token,
}: {
  userId: string;
  token: string;
}) {
  const password = useSignal("");
  const passwordConfirmation = useSignal("");
  const isSubmitting = useSignal(false);
  const errors = useSignal<Record<string, string>>({});
  const tokenError = useSignal<{ error: string; isExpired: boolean } | null>(
    null,
  );

  const formErrors = {
    get value() {
      return errors.value;
    },
    set value(v: Record<string, string>) {
      errors.value = v;
    },
  } as FormErrors;

  async function handleSubmit(e: Event) {
    e.preventDefault();

    // Client-side validation
    const newErrors: Record<string, string> = {};
    if (password.value.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    if (password.value !== passwordConfirmation.value) {
      newErrors.passwordConfirmation = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      errors.value = newErrors;
      return;
    }

    isSubmitting.value = true;
    errors.value = {};

    try {
      await rpc.profile.confirmPasswordChange({
        userId,
        token,
        password: password.value,
      });
      // Success - redirect to account page
      navigateTo("/account");
    } catch (err: unknown) {
      if (err instanceof ClientError && err.code === "invalid_token") {
        tokenError.value = {
          error: err.message,
          isExpired: true,
        };
      } else if (err && typeof err === "object" && "message" in err) {
        const error = err as { message: string; field?: string };
        if (error.field) {
          errors.value = { [error.field]: error.message };
        } else {
          errors.value = { form: error.message };
        }
      } else {
        errors.value = { form: "Something went wrong" };
      }
      isSubmitting.value = false;
    }
  }

  // Show error view if token is invalid
  if (tokenError.value) {
    return (
      <ErrorView
        error={tokenError.value.error}
        isExpired={tokenError.value.isExpired}
      />
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Enter your new password below."
    >
      <Form
        errors={formErrors}
        onSubmit={handleSubmit}
        class="flex flex-col gap-6"
      >
        {errors.value.form && (
          <div class="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {errors.value.form}
          </div>
        )}

        <Password
          label="New password"
          name="password"
          value={password.value}
          onInput={(e) => {
            password.value = (e.target as HTMLInputElement).value;
          }}
          autocomplete="new-password"
          autofocus
          required
        />

        <Password
          label="Confirm password"
          name="passwordConfirmation"
          value={passwordConfirmation.value}
          onInput={(e) => {
            passwordConfirmation.value = (e.target as HTMLInputElement).value;
          }}
          autocomplete="new-password"
          required
        />

        <Button type="submit" loading={isSubmitting.value}>
          Reset password
        </Button>
      </Form>
    </AuthLayout>
  );
}
